import React, {
  forwardRef,
  useCallback, useEffect, useImperativeHandle, useState,
} from 'react';
import styles from './searchInputTop.module.css';
import OptionsIcon from '../../assets/images/Icon-ionic-md-options.svg';
import Button from '../Button/button';
import SearchInputTopFilterModal, { LastSearch } from './SearchInputTopFilterModal';
import { ContractFlag, DocumentSearchDetailForm } from '../../services/http/documents.api';
import BaseText from '../BaseText/BaseText';
import { useAuthContext } from '../../store/auth.store';
import { useMessageModal } from '../../hooks/modal.hook';
import CustomSwitch from '../CustomSwitch/CustomSwitch';
import { WarehouseStatusCode } from '../../utils/warehouse.utils';
import { DocDetailCloseAreaClass } from '../../utils/document.utils';

export enum ContractType {
  ELECTRONIC = 'ELECTRONIC',
  PAPER = 'PAPER',
  ALL = 'ALL',
}

export enum WarehouseStatusType {
  NOT_STORED = 'NOT_STORED',
  STORED = 'STORED',
  ALL = 'ALL',
}

export enum FilterType {
  CONTRACT_TYPE = 'contract_type',
  WAREHOUSE_STATUS_TYPE = 'warehouse_status_type',
}

const warehouseStatusText: Record<WarehouseStatusType, string> = {
  NOT_STORED: '：未入庫',
  STORED: '：保管中',
  ALL: '',
};

const contractTypeText: Record<ContractType, string> = {
  ELECTRONIC: '：電子契約',
  PAPER: '：書面契約',
  ALL: '',
};

export const mapWarehouseStatusToCode = (statusType?: WarehouseStatusType): WarehouseStatusCode | undefined => {
  switch (statusType) {
    case WarehouseStatusType.NOT_STORED:
      return WarehouseStatusCode.NOT_RECEIVED;
    case WarehouseStatusType.STORED:
      return WarehouseStatusCode.IN_STORAGE;
    case WarehouseStatusType.ALL:
    default:
      return undefined;
  }
};

export const mapContractTypeToFlags = (
  contractType?: ContractType,
): ContractFlag | undefined => {
  switch (contractType) {
    case ContractType.ELECTRONIC:
      return 1;
    case ContractType.PAPER:
      return 0;
    case ContractType.ALL:
    default:
      return undefined;
  }
};

interface Props {
  text: string;
  isSearching: boolean;
  contractType: ContractType;
  warehouseStatus: WarehouseStatusType;
  onAdvancedSearch: (advancedSearch: DocumentSearchDetailForm) => void;
  onSearch: (text: string) => void;
  onTextChange: (text: string) => void;
  onChangeContractType: (contractType: ContractType, filterType: FilterType) => void;
  onChangeWarehouseStatus: (warehouseStatus: WarehouseStatusType, filterType: FilterType) => void;
  setIsOcrSearchEnabled?: (isOcrSearchEnabled: boolean) => void;
  isOcrSearchEnabled?: boolean;
  setLastAdvancedSearchForm?: (lastAdvancedSearchForm: LastSearch) => void;
  lastAdvancedSearchForm?: LastSearch | null;
  showOcrSwitch?: boolean;
  onChangeOcrStatus?: () => void;
  warehouseStatusAdvanced?: WarehouseStatusCode;
  setWarehouseStatusAdvanced?: (status: WarehouseStatusCode | undefined) => void;
}

export interface SearchInputTopRef {
  setShowAdvancedFilterMenu: React.Dispatch<React.SetStateAction<boolean>>;
}

function SearchInputTop({
  text,
  isSearching,
  contractType,
  warehouseStatus,
  onAdvancedSearch,
  onSearch,
  onTextChange,
  onChangeContractType,
  onChangeWarehouseStatus,
  setIsOcrSearchEnabled,
  isOcrSearchEnabled,
  setLastAdvancedSearchForm,
  lastAdvancedSearchForm,
  showOcrSwitch = false,
  onChangeOcrStatus,
  warehouseStatusAdvanced,
  setWarehouseStatusAdvanced,
}: Props, ref: React.Ref<SearchInputTopRef>) {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [showAdvancedFilterMenu, setShowAdvancedFilterMenu] = useState(false);

  const { user } = useAuthContext();

  const openMessageModal = useMessageModal();

  const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { value } = (e.target as HTMLInputElement);
    if (e.key === 'Enter') {
      if (value.length > 100) return;
      if (isOcrSearchEnabled && value.length <= 1) return;
      onSearch(text);
      Promise.resolve().then(() => {
        onTextChange(value);
      });
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { value } = (e.target as HTMLInputElement);
    if (e.key === 'Enter') {
      if (value.length > 100) openMessageModal('検索したいキーワードは100文字以下を指定してください。');
      if (isOcrSearchEnabled && value.length <= 1) openMessageModal('検索したいキーワードは2文字以上を指定してください。');
      (e.target as HTMLInputElement).blur();
    }
  };

  const toggleContractType = useCallback(() => {
    if (contractType === ContractType.ELECTRONIC) {
      onChangeContractType(ContractType.PAPER, FilterType.CONTRACT_TYPE);
    } else if (contractType === ContractType.PAPER) {
      onChangeContractType(ContractType.ALL, FilterType.CONTRACT_TYPE);
    } else {
      onChangeContractType(ContractType.ELECTRONIC, FilterType.CONTRACT_TYPE);
    }
  }, [contractType, onChangeContractType]);

  const toggleWarehouseStatus = useCallback(() => {
    if (warehouseStatus === WarehouseStatusType.NOT_STORED) {
      setWarehouseStatusAdvanced?.(WarehouseStatusCode.IN_STORAGE);
      onChangeWarehouseStatus(WarehouseStatusType.STORED, FilterType.WAREHOUSE_STATUS_TYPE);
    } else if (warehouseStatus === WarehouseStatusType.STORED) {
      setWarehouseStatusAdvanced?.(undefined);
      onChangeWarehouseStatus(WarehouseStatusType.ALL, FilterType.WAREHOUSE_STATUS_TYPE);
    } else {
      setWarehouseStatusAdvanced?.(WarehouseStatusCode.NOT_RECEIVED);
      onChangeWarehouseStatus(WarehouseStatusType.NOT_STORED, FilterType.WAREHOUSE_STATUS_TYPE);
    }
  }, [warehouseStatus, onChangeWarehouseStatus, setWarehouseStatusAdvanced]);

  useImperativeHandle(ref, () => ({
    setShowAdvancedFilterMenu,
  }));

  return (
    <div className={styles.container}>
      <div className={styles.searchBox}>
        <img className={styles.searchImg} alt="" src="images/search.svg" />
      </div>
      <div style={{ position: 'relative', flex: 1 }}>
        <input
          className={styles.input}
          value={text}
          onChange={(e) => onTextChange(e.target.value)}
          onKeyDown={onKeyPress}
          onFocus={() => setShowFilterMenu(true)}
          onBlur={() => setShowFilterMenu(false)}
          onKeyUp={handleKeyUp}
        />
        {
          (showFilterMenu && isSearching)
          && (
          <div className={styles['filter-dropdown']}>
            <Button
              className={styles['filter-button']}
              size="smaller"
              outline
              color={warehouseStatus === WarehouseStatusType.ALL ? 'lightGray' : 'orange'}
              background={warehouseStatus === WarehouseStatusType.ALL ? 'white' : 'lighterOrange'}
              onClick={toggleWarehouseStatus}
              onMouseDown={(e) => e.preventDefault()}
            >
              ステータス
              {warehouseStatusText[warehouseStatus]}
            </Button>
            <Button
              className={styles['filter-button']}
              size="smaller"
              outline
              color={contractType === 'ALL' ? 'lightGray' : 'orange'}
              background={contractType === 'ALL' ? 'white' : 'lighterOrange'}
              onClick={toggleContractType}
              onMouseDown={(e) => e.preventDefault()}
            >
              契約種別
              {contractTypeText[contractType]}
            </Button>
          </div>
          )
        }
      </div>
      <div className={styles['search-options-container']}>
        { (!!user?.hasOcrSupport && showOcrSwitch) && (
          <>
            <BaseText size="sm" color="#8c8282" style={{ marginBottom: 1, marginRight: 7 }}>全文検索</BaseText>
            <CustomSwitch
              checked={isOcrSearchEnabled}
              onChange={(event) => {
                const newValue = event.target.checked;
                localStorage.setItem('is_ocr_search_enabled', JSON.stringify(newValue));
                if (setIsOcrSearchEnabled) setIsOcrSearchEnabled(newValue);
                if (onChangeOcrStatus) onChangeOcrStatus();
              }}
            />
            <div />
            <div className={styles['vertical-divider']} />
          </>
        )}
        <img
          className={`${DocDetailCloseAreaClass} ${styles.filterButton}`}
          alt=""
          width="26"
          src={OptionsIcon}
          onClick={() => setShowAdvancedFilterMenu(!showAdvancedFilterMenu)}
        />
        {showAdvancedFilterMenu && (
          <SearchInputTopFilterModal
            open={showAdvancedFilterMenu}
            onAdvancedSearch={onAdvancedSearch}
            onClose={() => setShowAdvancedFilterMenu(false)}
            simpleSearchText={text}
            electronicContractProp={mapContractTypeToFlags(contractType)}
            setLastAdvancedSearchForm={setLastAdvancedSearchForm}
            lastAdvancedSearchForm={lastAdvancedSearchForm}
            isOcrSearchEnabled={isOcrSearchEnabled}
            setWarehouseStatesAdvanced={setWarehouseStatusAdvanced}
            warehouseStatusAdvanced={warehouseStatusAdvanced}
          />
        )}
      </div>
    </div>
  );
}

export default forwardRef(SearchInputTop);
